import React from "react";

import moment from "moment";
import { TFunction, useTranslation } from "react-i18next";

import { Table } from "antd";
import { SortOrder } from "antd/es/table/interface";
import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { ColumnTitle } from "../../../../components/table";
import { HostBase, HostItem } from "../../../../store/hosts/types";
import { formatNumber, getSortOrder } from "../../../../utils/table";

const columns = (t: TFunction<"hosts_page">, ordering: string, timestampFormat: string) => {
  return [
    {
      key: "date",
      title: <ColumnTitle title={t("table.columns.date.title")} helpText={t("table.columns.date.helpText")} />,
      dataIndex: "date",
      render: (value: string) => moment(value).format(timestampFormat),
      sorter: true,
      sortOrder: getSortOrder("date", ordering),
      showSorterTooltip: false,
    },
    {
      key: "searches",
      title: <ColumnTitle title={t("table.columns.searches.title")} helpText={t("table.columns.searches.helpText")} />,
      dataIndex: "searches",
      sorter: true,
      sortOrder: getSortOrder("searches", ordering),
      showSorterTooltip: false,
      render: (searches: number) => formatNumber(searches),
    },
    {
      key: "clicks",
      title: <ColumnTitle title={t("table.columns.clicks.title")} helpText={t("table.columns.clicks.helpText")} />,
      dataIndex: "clicks",
      sorter: true,
      sortOrder: getSortOrder("clicks", ordering),
      showSorterTooltip: false,
      render: (clicks: number) => formatNumber(clicks),
    },
    {
      key: "ctr",
      title: <ColumnTitle title={t("table.columns.ctr.title")} helpText={t("table.columns.ctr.helpText")} />,
      dataIndex: "ctr",
      sorter: true,
      sortOrder: getSortOrder("ctr", ordering),
      showSorterTooltip: false,
      render: (ctr: number) => formatNumber(ctr),
    },
    {
      key: "bc",
      title: <ColumnTitle title={t("table.columns.bc.title")} helpText={t("table.columns.bc.helpText")} />,
      dataIndex: "bc",
      sorter: true,
      sortOrder: getSortOrder("ctr", ordering),
      showSorterTooltip: false,
      render: (bc: number) => formatNumber(bc),
    },
    {
      key: "cbc",
      title: <ColumnTitle title={t("table.columns.cbc.title")} helpText={t("table.columns.cbc.helpText")} />,
      dataIndex: "cbc",
      sorter: true,
      sortOrder: getSortOrder("cbc", ordering),
      showSorterTooltip: false,
      render: (cbc: number) => formatNumber(cbc),
    },
    {
      key: "pbc",
      title: <ColumnTitle title={t("table.columns.pbc.title")} helpText={t("table.columns.pbc.helpText")} />,
      dataIndex: "pbc",
      sorter: true,
      sortOrder: getSortOrder("pbc", ordering),
      showSorterTooltip: false,
      render: (pbc: number) => formatNumber(pbc),
    },
    {
      key: "btr",
      title: <ColumnTitle title={t("table.columns.btr.title")} helpText={t("table.columns.btr.helpText")} />,
      dataIndex: "btr",
      sorter: true,
      sortOrder: getSortOrder("btr", ordering),
      showSorterTooltip: false,
      render: (btr: number) => formatNumber(btr),
    },
    {
      key: "str",
      title: <ColumnTitle title={t("table.columns.str.title")} helpText={t("table.columns.str.helpText")} />,
      dataIndex: "str",
      sorter: true,
      sortOrder: getSortOrder("str", ordering),
      showSorterTooltip: false,
      render: (str: number) => formatNumber(str),
    },
    {
      key: "productivity",
      title: (
        <ColumnTitle
          title={t("table.columns.productivity.title")}
          helpText={t("table.columns.productivity.helpText")}
        />
      ),
      dataIndex: "productivity",
      sorter: true,
      sortOrder: getSortOrder("productivity", ordering),
      showSorterTooltip: false,
      render: (productivity: number) => formatNumber(productivity),
    },
  ];
};

export interface DataTableProps {
  timestampFormat: string;
  total: number;
  dataSource: HostItem[];
  ordering: string;
  currentPage: number;
  pageSize?: number;
  loading?: boolean;
  onChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<HostItem> | SorterResult<HostItem>[],
    extra: TableCurrentDataSource<HostItem>
  ) => void;
  summary?: HostBase;
}

export const DataTable: React.FC<DataTableProps> = (props) => {
  const { t } = useTranslation("hosts_page");

  const summaryRows = (pageData: readonly HostItem[]): React.ReactNode => {
    if (pageData.length === 0) {
      return <></>;
    }
    let pageSummary = pageData.reduce((summary, item) => {
      if (Object.keys(summary).length === 0) {
        return item;
      }
      return {
        ...summary,
        searches: summary.searches + item.searches,
        clicks: summary.clicks + item.clicks,
        bc: summary.bc + item.bc,
        cbc: summary.cbc + item.cbc,
        pbc: summary.pbc + item.pbc,
        ctr: summary.ctr + item.ctr,
        btr: summary.btr + item.btr,
        str: summary.str + item.str,
        productivity: summary.productivity + item.productivity,
      };
    }, {} as HostItem);
    pageSummary = {
      ...pageSummary,
      ctr: pageSummary.ctr / pageData.length,
      btr: pageSummary.btr / pageData.length,
      str: pageSummary.str / pageData.length,
      productivity: pageSummary.productivity / pageData.length,
    };
    const cols = ["searches", "clicks", "ctr", "bc", "cbc", "pbc", "btr", "str", "productivity"] as Array<
      keyof HostBase
    >;

    return (
      <>
        <Table.Summary.Row>
          <Table.Summary.Cell index={1}>{t("table.summary.page")}</Table.Summary.Cell>
          {cols.map((field, idx) => {
            return (
              <Table.Summary.Cell key={`page-total-cell-${idx}`} index={idx + 1}>
                {formatNumber(pageSummary[field] as number)}
              </Table.Summary.Cell>
            );
          })}
        </Table.Summary.Row>
        <Table.Summary.Row>
          <Table.Summary.Cell index={101}>{t("table.summary.total")}</Table.Summary.Cell>
          {cols.map((field, idx) => {
            return (
              <Table.Summary.Cell key={`page-total-cell-${idx}`} index={idx + 1}>
                {props.summary && formatNumber(props.summary[field] as number)}
              </Table.Summary.Cell>
            );
          })}
        </Table.Summary.Row>
      </>
    );
  };

  return (
    <>
      <Table
        bordered
        loading={props.loading}
        columns={columns(t, props.ordering, props.timestampFormat)}
        sortDirections={["ascend", "descend", "ascend"] as SortOrder[]}
        dataSource={props.dataSource}
        onChange={props.onChange}
        rowKey={(record: HostItem) => `hosts-row-${record.date}`}
        pagination={{
          total: props.total,
          current: props.currentPage,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          pageSize: props.pageSize,
        }}
        size="small"
        summary={summaryRows}
      />
    </>
  );
};
